import { useLayoutEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useOrg } from "../lib/session";
import { ago } from "../lib/format";
import type { WaTemplate } from "../lib/types";
import { Empty, ErrorText, Field, Loading, PageHeader } from "../components/ui";
import { TemplateBubble, categoryNames, fillTemplate, languageNames } from "../components/WhatsAppTemplates";

interface ConnectionLite {
  id: string;
  phone_number_id: string;
  display_phone: string | null;
  display_name: string | null;
}

const statusText: Record<string, string> = {
  APPROVED: "Approved",
  PENDING: "In review",
  REJECTED: "Rejected",
  PAUSED: "Paused by Meta",
  DISABLED: "Disabled by Meta",
  IN_APPEAL: "In appeal",
};

// Turns "Appointment reminder!" into "appointment_reminder".
const slug = (s: string) => s.toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 100);

export default function Templates() {
  const { connectionId } = useParams();
  const { base, orgId, allows } = useOrg();
  const qc = useQueryClient();
  const key = [orgId, "templates", connectionId];
  const list = useQuery({ queryKey: key, queryFn: () => api<{ templates: WaTemplate[] }>(`${base}/channels/${connectionId}/templates`) });
  const conns = useQuery({ queryKey: [orgId, "channels"], queryFn: () => api<{ connections: ConnectionLite[] }>(`${base}/channels`) });
  const conn = conns.data?.connections.find((c) => c.id === connectionId);
  const [creating, setCreating] = useState(false);
  const [syncError, setSyncError] = useState<unknown>(null);
  const [syncing, setSyncing] = useState(false);
  const [deleteError, setDeleteError] = useState<unknown>(null);
  const canManage = allows("agents");
  const refresh = () => {
    qc.invalidateQueries({ queryKey: key });
    qc.invalidateQueries({ queryKey: [orgId, "wa-templates"] });
  };

  async function sync() {
    setSyncing(true);
    setSyncError(null);
    try {
      await api(`${base}/channels/${connectionId}/templates/sync`, { method: "POST" });
      refresh();
    } catch (err) {
      setSyncError(err);
    } finally {
      setSyncing(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Message templates"
        actions={canManage && (
          <>
            <button className="secondary" onClick={sync} disabled={syncing}>{syncing ? "Syncing…" : "Sync from Meta"}</button>
            <button onClick={() => setCreating(!creating)}>{creating ? "Close" : "New template"}</button>
          </>
        )}
      />
      <p className="muted">
        <Link to={`/o/${orgId}/channels`}>Channels</Link> · WhatsApp {conn ? conn.display_phone ?? conn.phone_number_id : ""}
        <br />
        Templates are messages Meta approves in advance. They're the only way to message a customer more than 24 hours after their last message: follow-ups, reminders, order updates, offers. Meta usually reviews a new template within minutes, sometimes up to a day.
      </p>
      <ErrorText error={syncError ?? deleteError} />
      {creating && <NewTemplate connectionId={connectionId!} onDone={() => { setCreating(false); refresh(); }} />}
      {list.isLoading ? <Loading /> : list.error ? <ErrorText error={list.error} /> : list.data!.templates.length === 0 ? (
        <Empty>No templates yet. Create one, or use "Sync from Meta" if you already made some in WhatsApp Manager.</Empty>
      ) : (
        <div className="template-grid">
          {list.data!.templates.map((t) => (
            <section key={t.id} className="card template-card">
              <div className="inline" style={{ justifyContent: "space-between" }}>
                <div>
                  <b>{t.name}</b>
                  <div className="muted small">{categoryNames[t.category]} · {languageNames[t.language] ?? t.language} · updated {ago(t.synced_at ?? t.created_at)}</div>
                </div>
                <span className={`tag status-${t.status.toLowerCase()}`}>{statusText[t.status] ?? t.status}</span>
              </div>
              <TemplateBubble header={t.header_text} body={fillTemplate(t.body_text, t.examples)} footer={t.footer_text} buttons={t.buttons} />
              {t.status === "REJECTED" && <p className="error small">Meta rejected it{t.rejected_reason ? `: ${t.rejected_reason.replace(/_/g, " ").toLowerCase()}` : ""}. Edit the wording and submit it again under a new name.</p>}
              {t.unsupported_reason && <p className="muted small">Can't be sent from SVENEX: {t.unsupported_reason}</p>}
              {canManage && (
                <button className="link small" onClick={async () => {
                  if (!confirm(`Delete "${t.name}"? It's also deleted in Meta, and the name can't be reused for 30 days.`)) return;
                  setDeleteError(null);
                  try {
                    await api(`${base}/channels/${connectionId}/templates/${t.id}`, { method: "DELETE" });
                    refresh();
                  } catch (err) {
                    setDeleteError(err);
                  }
                }}>Delete</button>
              )}
            </section>
          ))}
        </div>
      )}
    </>
  );
}

function NewTemplate({ connectionId, onDone }: { connectionId: string; onDone: () => void }) {
  const { base } = useOrg();
  const [label, setLabel] = useState("");
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [category, setCategory] = useState<"UTILITY" | "MARKETING">("UTILITY");
  const [language, setLanguage] = useState("en_US");
  const [header, setHeader] = useState("");
  const [body, setBody] = useState("");
  const [footer, setFooter] = useState("");
  const [buttons, setButtons] = useState<string[]>([]);
  const [examples, setExamples] = useState<string[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  // Where to put the caret after inserting a variable; applied right after
  // the re-render so typing continues after the variable.
  const caret = useRef<number | null>(null);
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (el && caret.current != null) {
      el.focus();
      el.setSelectionRange(caret.current, caret.current);
      caret.current = null;
    }
  }, [body]);

  const used = Array.from(body.matchAll(/\{\{\s*(\d+)\s*\}\}/g)).map((m) => Number(m[1]));
  const varCount = used.length ? Math.max(...used) : 0;
  const ex = Array.from({ length: varCount }, (_, i) => examples[i] ?? "");

  function addVariable() {
    const el = bodyRef.current;
    const token = `{{${varCount + 1}}}`;
    const at = el?.selectionStart ?? body.length;
    caret.current = at + token.length;
    setBody(body.slice(0, at) + token + body.slice(el?.selectionEnd ?? at));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api(`${base}/channels/${connectionId}/templates`, {
        method: "POST",
        json: { name, language, category, header_text: header, body_text: body, footer_text: footer, quick_replies: buttons.filter((b) => b.trim()), examples: ex },
      });
      onDone();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <h2>New template</h2>
      <form className="template-editor" onSubmit={submit}>
        <div>
          <Field label="What is it for?">
            <input value={label} placeholder="e.g. Appointment reminder" onChange={(e) => { setLabel(e.target.value); if (!nameTouched) setName(slug(e.target.value)); }} />
          </Field>
          <Field label="Template name (lowercase, underscores)">
            <input value={name} required pattern="[a-z0-9_]+" maxLength={512} onChange={(e) => { setNameTouched(true); setName(e.target.value); }} />
          </Field>
          <div className="row">
            <Field label="Type">
              <select value={category} onChange={(e) => setCategory(e.target.value as "UTILITY" | "MARKETING")}>
                <option value="UTILITY">Utility: updates, reminders, follow-ups</option>
                <option value="MARKETING">Marketing: offers, promotions, news</option>
              </select>
            </Field>
            <Field label="Language">
              <select value={language} onChange={(e) => setLanguage(e.target.value)}>
                <option value="en_US">English (US)</option>
                <option value="en_GB">English (UK)</option>
                <option value="ar">Arabic</option>
              </select>
            </Field>
          </div>
          <p className="muted small">
            {category === "UTILITY"
              ? "Utility messages are about something the customer asked for or bought. Meta re-labels promotional wording as Marketing."
              : "Marketing messages can only go to customers who opted in to WhatsApp marketing, and they reply STOP to opt out."}
          </p>
          <Field label="Header (optional)"><input value={header} maxLength={60} onChange={(e) => setHeader(e.target.value)} /></Field>
          <Field label="Message">
            <textarea ref={bodyRef} value={body} required rows={5} maxLength={1024} onChange={(e) => setBody(e.target.value)}
              placeholder="Hi {{1}}, this is a reminder of your appointment on {{2}}. Reply here if you need to change it." dir="auto" />
          </Field>
          <div className="inline">
            <button type="button" className="secondary small" onClick={addVariable}>Add variable {`{{${varCount + 1}}}`}</button>
            <span className="muted small">Variables are filled in when you send: a name, a date, an order number.</span>
          </div>
          {varCount > 0 && (
            <div className="examples">
              <p className="small"><b>Example values</b> <span className="muted">(Meta needs these to review the template)</span></p>
              {ex.map((v, i) => (
                <Field key={i} label={`{{${i + 1}}}`}>
                  <input value={v} required maxLength={200} placeholder={i === 0 ? "Ali" : ""} onChange={(e) => setExamples(ex.map((x, j) => (j === i ? e.target.value : x)))} />
                </Field>
              ))}
            </div>
          )}
          <Field label="Footer (optional)"><input value={footer} maxLength={60} placeholder="e.g. Smile Dental, Seef" onChange={(e) => setFooter(e.target.value)} /></Field>
          <div className="quick-replies">
            <p className="small"><b>Quick-reply buttons</b> <span className="muted">(optional, up to 3)</span></p>
            {buttons.map((b, i) => (
              <div key={i} className="inline">
                <input value={b} maxLength={25} placeholder="e.g. Confirm" onChange={(e) => setButtons(buttons.map((x, j) => (j === i ? e.target.value : x)))} />
                <button type="button" className="link" onClick={() => setButtons(buttons.filter((_, j) => j !== i))}>Remove</button>
              </div>
            ))}
            {buttons.length < 3 && <button type="button" className="secondary small" onClick={() => setButtons([...buttons, ""])}>Add button</button>}
          </div>
          <ErrorText error={error} />
          <button type="submit" disabled={busy}>{busy ? "Submitting…" : "Submit to Meta for review"}</button>
        </div>
        <div className="template-preview-col">
          <p className="small muted">Preview</p>
          <TemplateBubble header={header} body={fillTemplate(body, ex)} footer={footer} buttons={buttons.filter((b) => b.trim()).map((text) => ({ text }))} />
          <ul className="small muted tips">
            <li>Don't start or end the message with a variable.</li>
            <li>Be specific; vague or spammy wording gets rejected.</li>
            <li>A template can't be edited after approval. Create a new one instead.</li>
          </ul>
        </div>
      </form>
    </section>
  );
}
